import type { ConteoTerminoBusqueda } from '../services/tariff-catalog-service';

/**
 * Lo que el panel "Cómo entendimos tu búsqueda" necesita saber de cada término
 * para no mentir.
 *
 * El panel pintaba los términos todos iguales, en verde, aunque el arancel no
 * usara ninguno: el usuario veía "para trabajos de industria" como si hubiera
 * encontrado algo. Estas funciones separan los tres estados reales.
 */
export interface TerminoDelPanel {
  termino: string;
  /**
   * Cuántas partidas encontró. `null` = no se sabe (la respuesta no trajo
   * conteos, o el término no existe en el arancel y el número engañaría).
   */
  partidas: number | null;
  /** El arancel no usa esta frase: se descartó del puntaje. */
  sinRespaldo: boolean;
}

/**
 * Une los términos con los que se buscó con cuántas partidas encontró cada uno.
 *
 * Manda el orden de `terminosLegales` —es el que el usuario ya vio— y el
 * conteo se oculta cuando el término no tiene respaldo: `inexistente` en el
 * backend es `partidas === 0 || enElTexto === 0`, así que puede venir con
 * partidas > 0 por coincidencia suelta. Mostrar ese número al lado de la frase
 * sería decir que encontró algo cuando el arancel no la usa.
 */
export function terminosConConteo(
  terminosLegales: string[],
  conteos: ConteoTerminoBusqueda[] | undefined
): TerminoDelPanel[] {
  const porTermino = new Map((conteos ?? []).map((c) => [c.termino, c]));

  return terminosLegales.map((termino) => {
    const conteo = porTermino.get(termino);
    if (!conteo) return { termino, partidas: null, sinRespaldo: false };
    return {
      termino,
      partidas: conteo.inexistente ? null : conteo.partidas,
      sinRespaldo: conteo.inexistente,
    };
  });
}

/** Si hay alguno sin respaldo, el panel explica una sola vez qué significa. */
export function hayTerminosSinRespaldo(terminos: TerminoDelPanel[]): boolean {
  return terminos.some((t) => t.sinRespaldo);
}

/**
 * Las traducciones que vale la pena mostrar.
 *
 * La línea "Traduje" existe para explicar por qué se buscó OTRA cosa que la que
 * el usuario escribió, así que "cuero → cuero" no aporta: es ruido que le resta
 * peso a la traducción que sí importa ("botas → que cubran el tobillo").
 */
export function traduccionesVisibles(
  traducciones: Array<{ de: string; a: string }> | undefined
): Array<{ de: string; a: string }> {
  return (traducciones ?? []).filter(({ de, a }) => {
    const origen = de.trim();
    const destino = a.trim();
    if (!origen || !destino) return false;
    return clave(origen) !== clave(destino);
  });
}

/**
 * Los términos ignorados que de verdad se ignoraron.
 *
 * El intérprete devuelve las mismas palabras en `traducciones` y en
 * `descartado`, así que el panel llegó a decir a la vez "traduje botas" y
 * "ignoré botas" — medido en pantalla con `botas de cuero para trabajos de
 * industria`. Una palabra traducida no se ignoró: se cambió por otra, y eso ya
 * lo cuenta la línea "Traduje".
 *
 * Solo tapan las traducciones que se MUESTRAN: si una no se ve (traduce la
 * palabra a sí misma), sacarla también de acá la haría desaparecer de las dos
 * líneas y el usuario nunca sabría qué pasó con ella.
 */
export function descartadoVisible(
  descartado: string[] | undefined,
  traducciones: Array<{ de: string; a: string }> | undefined
): string[] {
  const traducidos = new Set(traduccionesVisibles(traducciones).map((t) => clave(t.de)));
  return (descartado ?? []).filter((t) => !traducidos.has(clave(t)));
}

/** Comparar términos que el usuario ve como iguales: sin espacios de más ni mayúsculas. */
function clave(termino: string): string {
  return termino.trim().toLocaleLowerCase();
}
